import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  FileEdit,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Copy,
  Eye,
  Calendar,
  Wand2,
  ArrowLeft,
  RefreshCw,
  BarChart3
} from 'lucide-react';

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent } from '../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { getAuthHeaders, adminFetch } from '../../../services/adminApi';
import { HtmlContentEditor } from '../shared';

import NewsletterAnalytics from './NewsletterAnalytics';
import MergeTagPicker from './MergeTagPicker';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileEdit },
  scheduled: { label: 'Scheduled', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  sending: { label: 'Sending', color: 'bg-blue-100 text-blue-800', icon: Loader2 },
  sent: { label: 'Sent', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: AlertCircle },
};

export default function NewsletterEditorTab({ showMessage, startComposing = false }) {
  const [newsletters, setNewsletters] = useState([]);
  const [lists, setLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(startComposing);
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    preview_text: '',
    target_list_ids: [],
    target_segment_ids: [],
    scheduled_send_at: ''
  });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatingAi, setGeneratingAi] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(false);

  useEffect(() => {
    fetchNewsletters();
    fetchLists();
    fetchSegments();
  }, []);

  const fetchNewsletters = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/api/admin/newsletter/newsletters`;
      if (statusFilter) url += `?status=${statusFilter}`;

      const response = await adminFetch(url, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setNewsletters(data);
      }
    } catch (error) {
      console.error('Error fetching newsletters:', error);
      showMessage('Failed to load newsletters', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLists = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/lists`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLists(data);
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
    }
  };

  const fetchSegments = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/segments`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSegments(data);
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
    }
  };

  // Image upload handler for WYSIWYG editor
  const handleEditorImageUpload = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await adminFetch(`${API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('googleAuthToken')}`
        },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        return data.url; // Return the CDN URL
      } else {
        console.error('Failed to upload image to S3');
        return null;
      }
    } catch (error) {
      console.error('Upload error:', error);
      return null;
    }
  };

  useEffect(() => {
    fetchNewsletters();
  }, [statusFilter]);

  const handleEdit = (newsletter) => {
    setEditing(newsletter);
    setFormData({
      subject: newsletter.subject,
      content: newsletter.content || '',
      preview_text: newsletter.preview_text || '',
      target_list_ids: newsletter.target_list_ids || [],
      target_segment_ids: newsletter.target_segment_ids || [],
      scheduled_send_at: newsletter.scheduled_send_at ? newsletter.scheduled_send_at.slice(0, 16) : ''
    });
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setFormData({
      subject: '',
      content: '',
      preview_text: '',
      target_list_ids: [],
      target_segment_ids: [],
      scheduled_send_at: ''
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.subject.trim()) {
      showMessage('Subject is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const url = editing
        ? `${API_BASE}/api/admin/newsletter/newsletters/${editing.id}`
        : `${API_BASE}/api/admin/newsletter/newsletters`;
      const method = editing ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        scheduled_send_at: formData.scheduled_send_at ? new Date(formData.scheduled_send_at).toISOString() : null
      };

      const response = await adminFetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const savedNewsletter = await response.json();
        showMessage(editing ? 'Newsletter saved' : 'Newsletter created');
        if (!editing) {
          setEditing(savedNewsletter);
        }
        fetchNewsletters();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to save newsletter', 'error');
      }
    } catch (error) {
      console.error('Error saving newsletter:', error);
      showMessage('Failed to save newsletter', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (newsletter) => {
    if (newsletter.status !== 'draft') {
      showMessage('Can only delete draft newsletters', 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${newsletter.subject}"?`)) {
      return;
    }

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/newsletters/${newsletter.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showMessage('Newsletter deleted');
        fetchNewsletters();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to delete newsletter', 'error');
      }
    } catch (error) {
      console.error('Error deleting newsletter:', error);
      showMessage('Failed to delete newsletter', 'error');
    }
  };

  const handleDuplicate = async (newsletter) => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/newsletters/${newsletter.id}/duplicate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showMessage('Newsletter duplicated');
        fetchNewsletters();
      } else {
        showMessage('Failed to duplicate newsletter', 'error');
      }
    } catch (error) {
      console.error('Error duplicating newsletter:', error);
      showMessage('Failed to duplicate newsletter', 'error');
    }
  };

  const handleSend = async (immediately = true) => {
    if (!editing) return;

    if (formData.target_list_ids.length === 0 && formData.target_segment_ids.length === 0) {
      showMessage('Please select at least one target list or segment', 'error');
      return;
    }

    if (!formData.content.trim()) {
      showMessage('Newsletter must have content', 'error');
      return;
    }

    if (!immediately && !formData.scheduled_send_at) {
      showMessage('Please set a scheduled send time', 'error');
      return;
    }

    const action = immediately ? 'send this newsletter now' : 'schedule this newsletter';
    if (!window.confirm(`Are you sure you want to ${action}?`)) {
      return;
    }

    // Save first
    await handleSave();

    setSending(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/newsletters/${editing.id}/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ send_immediately: immediately })
      });

      if (response.ok) {
        const result = await response.json();
        showMessage(immediately ? 'Newsletter is being sent!' : `Newsletter scheduled for ${formData.scheduled_send_at}`);
        setShowEditor(false);
        fetchNewsletters();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to send newsletter', 'error');
      }
    } catch (error) {
      console.error('Error sending newsletter:', error);
      showMessage('Failed to send newsletter', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (newsletter) => {
    if (!window.confirm(`Are you sure you want to resend "${newsletter.subject}" to its target lists?`)) {
      return;
    }

    setSending(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/newsletters/${newsletter.id}/resend`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showMessage('Newsletter is being resent!');
        fetchNewsletters();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to resend newsletter', 'error');
      }
    } catch (error) {
      console.error('Error resending newsletter:', error);
      showMessage('Failed to resend newsletter', 'error');
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async () => {
    if (!editing) {
      // Save first to get an ID
      await handleSave();
      return;
    }

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/newsletters/${editing.id}/preview`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setShowPreview(true);
      } else {
        showMessage('Failed to generate preview', 'error');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      showMessage('Failed to generate preview', 'error');
    }
  };

  const handleGenerateAi = async () => {
    if (!aiPrompt.trim()) {
      showMessage('Please enter a prompt describing what you want to generate', 'error');
      return;
    }

    setGeneratingAi(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/generate-content`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          prompt: aiPrompt,
          current_content: formData.content || null,
          tone: 'friendly'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setFormData({ ...formData, content: data.generated_content });
        setAiPrompt('');
        showMessage('Content generated successfully');

        if (data.suggestions?.length > 0) {
          console.log('AI Suggestions:', data.suggestions);
        }
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to generate content', 'error');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      showMessage('Failed to generate content', 'error');
    } finally {
      setGeneratingAi(false);
    }
  };

  const toggleListSelection = (listId) => {
    const current = formData.target_list_ids;
    if (current.includes(listId)) {
      setFormData({
        ...formData,
        target_list_ids: current.filter(id => id !== listId)
      });
    } else {
      setFormData({
        ...formData,
        target_list_ids: [...current, listId]
      });
    }
  };

  const toggleSegmentSelection = (segmentId) => {
    const current = formData.target_segment_ids;
    if (current.includes(segmentId)) {
      setFormData({
        ...formData,
        target_segment_ids: current.filter(id => id !== segmentId)
      });
    } else {
      setFormData({
        ...formData,
        target_segment_ids: [...current, segmentId]
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Editor View
  if (showEditor) {
    // Show analytics view for sent newsletters
    if (showAnalytics && editing && editing.status === 'sent') {
      return (
        <div className="space-y-6">
          {/* Analytics Header */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => setShowAnalytics(false)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Content
            </Button>
            <Button variant="ghost" onClick={() => { setShowEditor(false); setShowAnalytics(false); }}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Newsletters
            </Button>
          </div>
          <NewsletterAnalytics newsletter={editing} onClose={() => setShowAnalytics(false)} />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Editor Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => { setShowEditor(false); setShowAnalytics(false); }}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Newsletters
          </Button>
          <div className="flex gap-2">
            {editing && editing.status === 'sent' && (
              <Button variant="outline" onClick={() => setShowAnalytics(true)}>
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </Button>
            )}
            <Button variant="outline" onClick={handlePreview} disabled={!editing}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Draft
            </Button>
          </div>
        </div>

        {/* Subject */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="subject">Subject Line *</Label>
            <MergeTagPicker
              onInsert={(tag) => {
                const input = document.getElementById('subject');
                if (input) {
                  const start = input.selectionStart || formData.subject.length;
                  const end = input.selectionEnd || formData.subject.length;
                  const newSubject = formData.subject.slice(0, start) + tag + formData.subject.slice(end);
                  setFormData({ ...formData, subject: newSubject });
                  // Restore focus and cursor position
                  setTimeout(() => {
                    input.focus();
                    input.setSelectionRange(start + tag.length, start + tag.length);
                  }, 0);
                }
              }}
            />
          </div>
          <Input
            id="subject"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="e.g., January Newsletter - New Year, New Goals! (Use merge tags like {{first_name}})"
            className="text-lg"
          />
        </div>

        {/* Preview Text */}
        <div className="space-y-2">
          <Label htmlFor="preview_text">Preview Text (optional)</Label>
          <Input
            id="preview_text"
            value={formData.preview_text}
            onChange={(e) => setFormData({ ...formData, preview_text: e.target.value })}
            placeholder="Short preview text shown in email clients..."
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">This appears after the subject line in most email clients</p>
        </div>

        {/* AI Content Generation */}
        <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="h-5 w-5 text-purple-600" />
              <span className="font-medium text-purple-900">AI Content Assistant</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Describe what you want to write... (e.g., 'Monthly update about belt tests, upcoming events, and holiday schedule')"
                className="flex-1"
              />
              <Button
                onClick={handleGenerateAi}
                disabled={generatingAi}
                variant="adminPrimary"
              >
                {generatingAi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-purple-700 mt-2">
              AI will generate or modify content based on your prompt. You can describe themes, styles, or specific content.
            </p>
          </CardContent>
        </Card>

        {/* Content Editor */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label>Newsletter Content</Label>
            <MergeTagPicker
              onInsert={(tag) => {
                // Append tag to content (user can position it in editor)
                // For more precise insertion, we'd need editor ref access
                const newContent = formData.content + tag;
                setFormData({ ...formData, content: newContent });
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Use merge tags like <code className="bg-purple-50 text-purple-700 px-1 rounded">{'{{first_name}}'}</code> to personalize emails.
            Tags are replaced with recipient data when sent.
          </p>
          <div className="border rounded-lg">
            <HtmlContentEditor
              value={formData.content}
              onChange={(content) => setFormData({ ...formData, content })}
              placeholder="Write your newsletter content here..."
              onImageUpload={handleEditorImageUpload}
            />
          </div>
        </div>

        {/* Target Lists & Segments */}
        <div className="space-y-4">
          <Label>Target Audiences *</Label>
          <p className="text-xs text-gray-500 dark:text-gray-400">Select lists and/or segments to target. Recipients are deduplicated automatically.</p>

          {/* Lists */}
          {lists.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm text-gray-600 dark:text-gray-400">Email Lists</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {lists.map(list => (
                  <label key={list.id} className="flex items-center gap-2 p-3 border dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.target_list_ids.includes(list.id)}
                      onChange={() => toggleListSelection(list.id)}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div>
                      <span className="text-sm font-medium">{list.name}</span>
                      <span className="text-xs text-gray-500 ml-2">({list.recipient_count})</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Segments */}
          {segments.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm text-gray-600 dark:text-gray-400">Dynamic Segments</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {segments.map(segment => (
                  <label key={segment.id} className="flex items-center gap-2 p-3 border border-blue-200 rounded-lg hover:bg-blue-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.target_segment_ids.includes(segment.id)}
                      onChange={() => toggleSegmentSelection(segment.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium">{segment.name}</span>
                      <span className="text-xs text-blue-600 ml-2">({segment.recipient_count})</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {lists.length === 0 && segments.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">No lists or segments available. Create a list or segment first.</p>
          )}
        </div>

        {/* Schedule */}
        <div className="space-y-2">
          <Label htmlFor="scheduled_send_at">Schedule Send (optional)</Label>
          <Input
            id="scheduled_send_at"
            type="datetime-local"
            value={formData.scheduled_send_at}
            onChange={(e) => setFormData({ ...formData, scheduled_send_at: e.target.value })}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">Leave empty to send immediately when clicking Send</p>
        </div>

        {/* Send Actions */}
        {editing && editing.status === 'draft' && (
          <div className="flex gap-2 pt-4 border-t">
            <Button
              onClick={() => handleSend(true)}
              disabled={sending}
              variant="adminPrimary"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send Now
            </Button>
            {formData.scheduled_send_at && (
              <Button
                variant="outline"
                onClick={() => handleSend(false)}
                disabled={sending}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Schedule
              </Button>
            )}
          </div>
        )}

        {/* Resend Action for Sent Newsletters */}
        {editing && editing.status === 'sent' && (
          <div className="flex gap-2 pt-4 border-t">
            <Button
              onClick={() => handleResend(editing)}
              disabled={sending}
              variant="adminPrimary"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Resend Newsletter
            </Button>
            <p className="text-sm text-gray-500 self-center ml-2">
              Originally sent: {formatDate(editing.sent_at)}
            </p>
          </div>
        )}

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Email Preview</DialogTitle>
            </DialogHeader>
            <div className="mt-4 border rounded-lg p-4 bg-background">
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>
        <Button onClick={handleCreate} variant="adminPrimary">
          <Plus className="h-4 w-4 mr-2" />
          New Newsletter
        </Button>
      </div>

      {/* Newsletters List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : newsletters.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <FileEdit className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No newsletters yet</p>
          <p className="text-sm">Create your first newsletter to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {newsletters.map((newsletter) => {
            const status = STATUS_CONFIG[newsletter.status] || STATUS_CONFIG.draft;
            const StatusIcon = status.icon;
            return (
              <Card key={newsletter.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{newsletter.subject}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
                          <StatusIcon className={`h-3 w-3 ${newsletter.status === 'sending' ? 'animate-spin' : ''}`} />
                          {status.label}
                        </span>
                      </div>
                      {newsletter.preview_text && (
                        <p className="text-sm text-gray-500 truncate">{newsletter.preview_text}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span>Created: {formatDate(newsletter.created_at)}</span>
                        {newsletter.sent_at && (
                          <span>Sent: {formatDate(newsletter.sent_at)}</span>
                        )}
                        {newsletter.status === 'scheduled' && newsletter.scheduled_send_at && (
                          <span className="text-yellow-600">Scheduled: {formatDate(newsletter.scheduled_send_at)}</span>
                        )}
                        {newsletter.send_stats?.sent_count > 0 && (
                          <span className="text-green-600">{newsletter.send_stats.sent_count} sent</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4">
                      {newsletter.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(newsletter)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      )}
                      {newsletter.status !== 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(newsletter)}
                          className="h-8 w-8 p-0"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicate(newsletter)}
                        className="h-8 w-8 p-0"
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {newsletter.status === 'sent' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              handleEdit(newsletter);
                              setShowAnalytics(true);
                            }}
                            className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700"
                            title="Analytics"
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResend(newsletter)}
                            disabled={sending}
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                            title="Resend"
                          >
                            <RefreshCw className={`h-4 w-4 ${sending ? 'animate-spin' : ''}`} />
                          </Button>
                        </>
                      )}
                      {newsletter.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(newsletter)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
